import axios from 'axios'
import cookies from 'js-cookie'
import * as rax from 'retry-axios'
import { setupCache } from 'axios-cache-interceptor'
import store from 'store'
import qs from 'qs'
import { setAuthCookie, removeAuthCookie } from '@/utils/authCookie'

const baseDomain = location.hostname.split('.').slice(-2).join('.')
const apiDomain = import.meta.env.VITE_API_DOMAIN ?? `newsdesk-api.${baseDomain}`
const apiDomainProtocol = import.meta.env.VITE_API_DOMAIN_PROTOCOL ?? `https`
const loginUrl = import.meta.env.VITE_LOGIN_URL ?? `https://${apiDomain}/okta/login`

const api = setupCache(
  axios.create({
    baseURL: `${apiDomainProtocol}://${apiDomain}`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8'
    },
    timeout: 50000,
    paramsSerializer: (params) => {
      return qs.stringify(params, {
        arrayFormat: 'brackets',
        filter: (prefix, value) => (value === null || typeof value === 'undefined' ? undefined : value) // remove null and undefined values
      })
    }
  })
)

// Add a request interceptor to add cache params
api.interceptors.request.use((config) => {
  // old axios-cache-adapter params:
  // cache: {
  //   maxAge: 5 * 1000, // 5 second default
  //   // debug: true,
  //   readHeaders: false,
  //   exclude: {
  //     query: false
  //   },
  //   ignoreCache: false,
  // }
  // default params
  let defaultCache = {
    ttl: 5 * 1000, // 5 second default --- Equivalent to `maxAge: false`
    interpretHeader: false, // --- Equivalent to `readHeaders: false`
    cachePredicate: {
      ignoreUrls: [] // allow caching of query parameter requests --- Equivalent to `exclude: { query: false }`
    },
    override: false, // when this option is 'true', it bypasses the current cache and always make a new http request --- Equivalent to `ignoreCache: false`
    // axios-cache-interceptor defaults this to true, which turns any failed GET into the
    // previous successful response when a cache entry (even one still inside the 5s TTL)
    // exists for the same key. Callers here handle errors themselves, and a request that
    // asked for `ignoreCache` must see the real error: the script editor's schema-version
    // 409 was being replaced by the earlier 200 whenever a script was reopened within 5s.
    staleIfError: false
  }
  if (typeof config?.cache?.maxAge !== 'undefined') {
    defaultCache.ttl = config.cache.maxAge
  }
  if (typeof config?.cache?.exclude?.query !== 'undefined') {
    if (config.cache.exclude.query) {
      defaultCache.cachePredicate.ignoreUrls = [/\?.+/] // // Ignore URLs that contain "?" (i.e., have query parameters)
    }
  }
  if (typeof config?.cache?.ignoreCache !== 'undefined') {
    defaultCache.override = config.cache.ignoreCache // This option bypasses the current cache and always make a new http request. This will not delete the current cache, it will just replace the cache when the response arrives.
    // or
    // defaultCache = false // this option will disable the cache for this request. This does not mean that the cache will be excluded from the storage.
  }

  config.cache = defaultCache
  return config
})

// Default axios retry configuration.  Currently retry is set to 0 so
// retries can be scoped to individual calls.  The other defaults
// still apply unless overridden in specific calls.
api.defaults.raxConfig = {
  instance: api,

  // Retry times on requests that return a response (500, etc) before giving up.
  retry: 0,

  // Retry twice on errors that don't return a response (ENOTFOUND, ETIMEDOUT, etc).
  noResponseRetries: 2,

  // Milliseconds to delay at first.  Defaults to 100. Only considered when backoffType is 'static'
  retryDelay: 500,

  backoffType: 'static', // 'exponential' (default), 'static' or 'linear'

  // Response status codes to retry.  Supports a double array with a list of ranges.
  statusCodesToRetry: [
    [400, 429],
    [500, 599]
  ],

  // HTTP methods to automatically retry.
  httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'DELETE', 'PUT', 'PATCH', 'POST'],

  onRetryAttempt: (err) => {
    const cfg = rax.getConfig(err)
    console.debug(`Retry attempt #${cfg.currentRetryAttempt}`)
  }
}

rax.attach(api)

// Add a request interceptor to add the Authorization header and CSRF token
api.interceptors.request.use((config) => {
  const accessToken = cookies.get('EWS_AUTH_KEY')
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Add CSRF token for state-changing requests
  // Laravel sets XSRF-TOKEN cookie, we need to send it in X-CSRF-TOKEN header
  const csrfToken = cookies.get('XSRF-TOKEN')
  if (csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method.toUpperCase())) {
    config.headers['X-CSRF-TOKEN'] = csrfToken
  }

  return config
})

// One refresh at a time. Every caller (the 401 interceptor below, and the
// 3 minute timer and on-load refresh in the default layout via the Vuex
// action) shares the in-flight request, so a page load that 401s on several
// requests at once still sends a single /v2/auth/refresh_token. The cookie is
// only rewritten when the response carries a well-formed token: a 200 with an
// empty or HTML body (seen during a pod rotation) used to store the string
// "undefined" and log every open tab out (ND-2362).
let refreshInFlight = null

export function refreshAuthToken() {
  if (!refreshInFlight) {
    refreshInFlight = api
      .get('/v2/auth/refresh_token', {
        // Signal Okta JWT support so the backend returns an Okta-signed access
        // token instead of the legacy self-signed JWT.
        headers: { 'X-Supports-Okta-JWT': 'true' },
        // Always hit the network; a cached token is never what a refresh wants.
        cache: { ignoreCache: true }
      })
      .then((response) => {
        const jwt = response?.data?.jwt
        // Throws on anything that is not a JWT, leaving the previous cookie in place.
        setAuthCookie(jwt)
        return jwt
      })
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

function clearSessionAndLogin() {
  store.remove('ews_user')
  store.remove('ews_current_station')
  store.remove('ews_station_settings')
  store.remove('ews_container')
  removeAuthCookie()

  const prev = encodeURIComponent(location.href)
  window.location.href = `${loginUrl}?prev=${prev}&supports_okta_jwt=true`
}

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    if (401 === error?.response?.status && error?.config?.baseURL?.includes('api.scrippsnet')) {
      // if we got a 401 on the refresh token endpoint, go to login
      if (originalRequest.url === '/v2/auth/refresh_token') {
        console.log('401 error on retry', error)
        clearSessionAndLogin()
        return Promise.reject(error)
      }

      // Already retried once with a freshly refreshed token and still 401: the
      // request is unauthorized on its own merits. Retrying again would loop
      // refresh -> retry -> 401 indefinitely.
      if (originalRequest._retry) {
        console.warn('401 after token refresh, not retrying', originalRequest.url)
        return Promise.reject(error)
      }
      originalRequest._retry = true

      console.log('401 error', error)
      try {
        const jwt = await refreshAuthToken()

        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${jwt}`
        return api(originalRequest)
      } catch (err) {
        // Only a definitive rejection (401/422) means the session is dead.
        // Network errors (no response), 5xx (including the backend's 503 when
        // Okta is unreachable) and a malformed refresh body are transient: keep
        // the cookie so the next scheduled refresh can recover instead of
        // forcing a re-login over a blip (e.g. laptop sleep mid-refresh).
        const refreshStatus = err?.response?.status
        if (refreshStatus === 401 || refreshStatus === 422) {
          clearSessionAndLogin()
        }
        return Promise.reject(err)
      }
    }

    return Promise.reject(error)
  }
)

export default api
